import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import {
  Compass,
  BookOpen,
  Sparkles,
  Loader2,
  AlertCircle,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Archive,
  RotateCcw,
  Info,
  Link2,
  FilePlus2,
  History,
  X,
  Languages,
  Landmark,
  Quote,
  Sprout,
  Type,
  GitBranch,
  Music,
  Users,
  BadgeCheck,
  AlertTriangle,
  Lightbulb,
  Cross,
  Target,
  Printer,
  Download,
  Square,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { logger } from '../../lib/logger';
import { fetchBiblePassage, bibleVersions } from '../../lib/bible';
import { BibleSelector } from '../BibleSelector';
import { SaveNotification } from '../SaveNotification';
import { useAuth } from '../../hooks/useAuth';
import { createStyledPDF, addSection, cleanAIArray } from '../../lib/pdf';

// ── Public types ─────────────────────────────────────────────────────────────

export interface PassageGuideContent {
  reference: string;
  bigIdea: string;
  sourcesUsed: { name: string; tradition: string; whyRelevant: string }[];
  wordStudy: {
    language: 'greek' | 'hebrew' | 'mixed';
    groundedInRobertson: boolean;
    entries: {
      word: string;
      transliteration: string;
      gloss: string;
      verse: string;
      significance: string;
      confidence: 'grounded' | 'general' | 'verify';
    }[];
  };
  context: { historical: string; literary: string; canonical: string };
  insights: { insight: string; application: string; attributedTo: string | null }[];
  applications: {
    audience: string;
    situation: string;
    application: string;
    rootedIn: string;
    tension: string;
    illustrationSeed: string;
  }[];
  seedPoints: { seed: string; angle: string }[];
  titles: string[];
  crossReferences: { reference: string; connection: string }[];
  christCentered: string;
  commonMisreadings: { misreading: string; correction: string }[];
  serviceAssets: { songs: { title: string; artist: string; reason: string }[]; readings: string[] };
}

interface PassageGuideProps {
  mode?: 'page' | 'panel';
  defaultSermonId?: string;
  sermonScripture?: string;
}

// ── Internal types ───────────────────────────────────────────────────────────

interface PassageGuideRow {
  id: string;
  user_id: string;
  sermon_id: string | null;
  title: string;
  scripture_reference: string;
  translation: string | null;
  denomination_used: string | null;
  content: unknown;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

interface ActiveGuide {
  id: string | null;
  sermonId: string | null;
  scriptureReference: string;
  translation: string | null;
  denominationUsed: string | null;
  content: PassageGuideContent;
  createdAt: string;
}

interface Section {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: React.ReactNode;
  node: React.ReactNode;
  copyText: string;
}

// ── Constants ────────────────────────────────────────────────────────────────

const LOADING_STAGES = [
  'Reading the passage…',
  'Consulting sources in your tradition…',
  'Writing your brief…',
] as const;

const STAGE_INTERVAL_MS = 22000;

// ── Defensive normalization ─────────────────────────────────────────────────
// The generated JSON (and any older saved record) may be missing or malformed
// fields — this guarantees a fully-shaped PassageGuideContent so rendering
// never has to guard against undefined.

function normalizeContent(raw: unknown): PassageGuideContent {
  const r = (raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}) as Record<string, unknown>;
  const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);

  const ws = (r.wordStudy && typeof r.wordStudy === 'object' ? r.wordStudy : {}) as Record<string, unknown>;
  const ctx = (r.context && typeof r.context === 'object' ? r.context : {}) as Record<string, unknown>;
  const sa = (r.serviceAssets && typeof r.serviceAssets === 'object' ? r.serviceAssets : {}) as Record<string, unknown>;
  const language = ws.language;

  return {
    reference: str(r.reference),
    bigIdea: str(r.bigIdea),
    sourcesUsed: arr<PassageGuideContent['sourcesUsed'][number]>(r.sourcesUsed),
    wordStudy: {
      language: language === 'greek' || language === 'hebrew' || language === 'mixed' ? language : 'mixed',
      groundedInRobertson: ws.groundedInRobertson === true,
      entries: arr<PassageGuideContent['wordStudy']['entries'][number]>(ws.entries),
    },
    context: {
      historical: str(ctx.historical),
      literary: str(ctx.literary),
      canonical: str(ctx.canonical),
    },
    insights: arr<PassageGuideContent['insights'][number]>(r.insights),
    applications: arr<PassageGuideContent['applications'][number]>(r.applications),
    seedPoints: arr<PassageGuideContent['seedPoints'][number]>(r.seedPoints),
    titles: arr<unknown>(r.titles).filter((t): t is string => typeof t === 'string'),
    crossReferences: arr<PassageGuideContent['crossReferences'][number]>(r.crossReferences),
    christCentered: str(r.christCentered),
    commonMisreadings: arr<PassageGuideContent['commonMisreadings'][number]>(r.commonMisreadings),
    serviceAssets: {
      songs: arr<PassageGuideContent['serviceAssets']['songs'][number]>(sa.songs),
      readings: arr<unknown>(sa.readings).filter((t): t is string => typeof t === 'string'),
    },
  };
}

function rowToActiveGuide(row: PassageGuideRow): ActiveGuide {
  return {
    id: row.id,
    sermonId: row.sermon_id,
    scriptureReference: row.scripture_reference,
    translation: row.translation,
    denominationUsed: row.denomination_used,
    content: normalizeContent(row.content),
    createdAt: row.created_at,
  };
}

// ── Small presentational helpers ────────────────────────────────────────────

function isAbortError(err: unknown): boolean {
  return typeof err === 'object' && err !== null && 'name' in err && (err as { name?: unknown }).name === 'AbortError';
}

function EmptySectionNote({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-slate/50 dark:text-soft-white/40 italic">{children}</p>;
}

function ConfidenceBadge({ confidence }: { confidence: string }) {
  if (confidence !== 'verify') return null;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap">
      <AlertTriangle className="h-2.5 w-2.5" /> Verify in a lexicon
    </span>
  );
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!text.trim()) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => { /* clipboard access denied — silently ignore */ });
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={`Copy ${label} to clipboard`}
      className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-slate/60 dark:text-soft-white/40 hover:text-electric hover:bg-electric/5 transition-colors"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function DenominationNudge() {
  return (
    <div className="flex items-start gap-2 px-3.5 py-2.5 rounded-lg bg-electric/5 border border-electric/20 text-xs text-slate dark:text-soft-white/60">
      <Info className="h-3.5 w-3.5 text-electric flex-shrink-0 mt-0.5" />
      <span>
        Set your denomination in{' '}
        <Link to="/dashboard/settings" className="text-electric font-medium hover:underline">
          Settings → Account
        </Link>{' '}
        to get insights from your tradition.
      </span>
    </div>
  );
}

function StagedLoadingBlock({ stageIndex }: { stageIndex: number }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
      <Loader2 className="h-7 w-7 animate-spin text-electric" />
      <p className="text-sm font-medium text-slate dark:text-soft-white/60">{LOADING_STAGES[stageIndex]}</p>
      <p className="text-xs text-slate/40 dark:text-soft-white/30 max-w-xs">
        This brief takes a minute or two to research and write well — thanks for your patience.
      </p>
    </div>
  );
}

// ── Section builders ─────────────────────────────────────────────────────────

function buildSections(content: PassageGuideContent, onUseTitle?: (title: string) => void): Section[] {
  const sections: Section[] = [];

  sections.push({
    id: 'bigIdea',
    label: 'Big Idea',
    icon: Lightbulb,
    node: (
      <div className="relative pl-5 sm:pl-6">
        <div className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full bg-gold/50" />
        <p className="font-serif text-xl sm:text-2xl leading-snug text-midnight dark:text-soft-white">
          {content.bigIdea || 'No summary was generated for this passage.'}
        </p>
      </div>
    ),
    copyText: content.bigIdea,
  });

  sections.push({
    id: 'sources',
    label: 'Sources Used',
    icon: Users,
    node:
      content.sourcesUsed.length === 0 ? (
        <EmptySectionNote>No sources were suggested.</EmptySectionNote>
      ) : (
        <div className="space-y-3">
          {content.sourcesUsed.map((s, i) => (
            <div key={i} className="rounded-lg border border-sand/20 dark:border-slate/20 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold text-midnight dark:text-soft-white">
                  {s.name || 'Unnamed source'}
                </span>
                {s.tradition && (
                  <span className="text-[10px] uppercase tracking-wide text-slate/50 dark:text-soft-white/40">
                    {s.tradition}
                  </span>
                )}
              </div>
              {s.whyRelevant && (
                <p className="text-xs text-slate dark:text-soft-white/60 mt-1 leading-relaxed">{s.whyRelevant}</p>
              )}
            </div>
          ))}
        </div>
      ),
    copyText: content.sourcesUsed
      .map(s => `${s.name}${s.tradition ? ` (${s.tradition})` : ''}${s.whyRelevant ? ` — ${s.whyRelevant}` : ''}`)
      .join('\n'),
  });

  const groundedBadge = content.wordStudy.groundedInRobertson ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald/10 text-emerald border border-emerald/20 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap">
      <BadgeCheck className="h-3 w-3" /> Grounded in A.T. Robertson
    </span>
  ) : undefined;

  sections.push({
    id: 'wordStudy',
    label: 'Word Study',
    icon: Languages,
    badge: groundedBadge,
    node: (
      <div className="space-y-3">
        <p className="text-xs italic text-slate/50 dark:text-soft-white/40 capitalize">
          {content.wordStudy.language} word study
        </p>
        {content.wordStudy.entries.length === 0 ? (
          <EmptySectionNote>No word study entries were generated.</EmptySectionNote>
        ) : (
          content.wordStudy.entries.map((e, i) => (
            <div key={i} className="rounded-lg border border-sand/20 dark:border-slate/20 px-4 py-3.5">
              <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                <span className="font-serif text-xl text-midnight dark:text-soft-white">{e.word || '—'}</span>
                {e.transliteration && (
                  <span className="text-sm italic text-slate/60 dark:text-soft-white/40">{e.transliteration}</span>
                )}
                {e.verse && (
                  <span className="text-xs text-slate/40 dark:text-soft-white/30 ml-auto">{e.verse}</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                {e.gloss && (
                  <p className="text-sm italic text-midnight/90 dark:text-soft-white/80">{e.gloss}</p>
                )}
                <ConfidenceBadge confidence={e.confidence} />
              </div>
              {e.significance && (
                <p className="text-sm text-slate dark:text-soft-white/60 mt-2 leading-relaxed">{e.significance}</p>
              )}
            </div>
          ))
        )}
      </div>
    ),
    copyText: content.wordStudy.entries
      .map(e => `${e.word} (${e.transliteration}) — ${e.gloss} [${e.verse}]\n${e.significance}`)
      .join('\n\n'),
  });

  sections.push({
    id: 'context',
    label: 'Context',
    icon: Landmark,
    node: (
      <div className="space-y-4">
        <div>
          <h4 className="text-xs font-semibold text-slate/50 dark:text-soft-white/40 uppercase tracking-wider mb-1">
            Historical
          </h4>
          <p className="text-sm text-midnight dark:text-soft-white/80 leading-relaxed">
            {content.context.historical || '—'}
          </p>
        </div>
        <div>
          <h4 className="text-xs font-semibold text-slate/50 dark:text-soft-white/40 uppercase tracking-wider mb-1">
            Literary
          </h4>
          <p className="text-sm text-midnight dark:text-soft-white/80 leading-relaxed">
            {content.context.literary || '—'}
          </p>
        </div>
        <div>
          <h4 className="text-xs font-semibold text-slate/50 dark:text-soft-white/40 uppercase tracking-wider mb-1">
            Canonical
          </h4>
          <p className="text-sm text-midnight dark:text-soft-white/80 leading-relaxed">
            {content.context.canonical || '—'}
          </p>
        </div>
      </div>
    ),
    copyText: `Historical:\n${content.context.historical}\n\nLiterary:\n${content.context.literary}\n\nCanonical:\n${content.context.canonical}`,
  });

  sections.push({
    id: 'insights',
    label: 'Insights',
    icon: Quote,
    node:
      content.insights.length === 0 ? (
        <EmptySectionNote>No insights were generated.</EmptySectionNote>
      ) : (
        <div className="space-y-3">
          {content.insights.map((ins, i) => (
            <div key={i} className="rounded-lg border border-sand/20 dark:border-slate/20 p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-midnight dark:text-soft-white leading-relaxed">{ins.insight}</p>
                {ins.attributedTo && (
                  <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-electric/10 text-electric text-[10px] font-semibold whitespace-nowrap">
                    {ins.attributedTo}
                  </span>
                )}
              </div>
              {ins.application && (
                <p className="text-xs text-slate dark:text-soft-white/50 mt-2">
                  <span className="font-semibold text-emerald">Application:</span> {ins.application}
                </p>
              )}
            </div>
          ))}
        </div>
      ),
    copyText: content.insights
      .map(i => `${i.insight}\nApplication: ${i.application}${i.attributedTo ? `\n— ${i.attributedTo}` : ''}`)
      .join('\n\n'),
  });

  sections.push({
    id: 'applications',
    label: 'Application',
    icon: Target,
    node:
      content.applications.length === 0 ? (
        <EmptySectionNote>No applications were generated.</EmptySectionNote>
      ) : (
        <div className="space-y-4">
          {content.applications.map((a, i) => (
            <div
              key={i}
              className="rounded-xl border border-electric/20 dark:border-electric/25 bg-electric/[0.04] dark:bg-electric/[0.07] p-4 sm:p-5"
            >
              {(a.audience || a.situation) && (
                <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 mb-2">
                  {a.audience && (
                    <span className="text-sm font-semibold text-midnight dark:text-soft-white">{a.audience}</span>
                  )}
                  {a.situation && (
                    <span className="text-sm text-slate dark:text-soft-white/60">
                      {a.audience ? '— ' : ''}
                      {a.situation}
                    </span>
                  )}
                </div>
              )}
              {a.application && (
                <p className="text-base font-medium text-midnight dark:text-soft-white leading-relaxed">
                  {a.application}
                </p>
              )}
              {a.rootedIn && (
                <p className="mt-2.5 text-xs font-serif italic text-gold">{a.rootedIn}</p>
              )}
              {a.tension && (
                <p className="mt-1.5 text-xs italic text-slate/60 dark:text-soft-white/40">{a.tension}</p>
              )}
              {a.illustrationSeed && (
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-gold/10 border border-gold/20 px-3 py-2">
                  <Lightbulb className="h-3.5 w-3.5 text-gold flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-slate dark:text-soft-white/70">
                    <span className="font-semibold text-gold">Illustration seed:</span> {a.illustrationSeed}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      ),
    copyText: content.applications
      .map(a =>
        [
          `${a.audience}${a.situation ? ` — ${a.situation}` : ''}`,
          a.application,
          a.rootedIn ? `Rooted in: ${a.rootedIn}` : null,
          a.tension ? `Tension: ${a.tension}` : null,
          a.illustrationSeed ? `Illustration seed: ${a.illustrationSeed}` : null,
        ]
          .filter(Boolean)
          .join('\n')
      )
      .join('\n\n'),
  });

  sections.push({
    id: 'seedPoints',
    label: 'Seed Points',
    icon: Sprout,
    node: (
      <div className="space-y-3">
        <p className="text-xs italic text-slate/60 dark:text-soft-white/40">
          Seed ideas to spark your own study — not an outline
        </p>
        {content.seedPoints.length === 0 ? (
          <EmptySectionNote>No seed points were generated.</EmptySectionNote>
        ) : (
          <ul className="space-y-2">
            {content.seedPoints.map((sp, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Sprout className="h-3.5 w-3.5 text-emerald flex-shrink-0 mt-0.5" />
                <span className="text-midnight dark:text-soft-white">
                  {sp.seed}
                  {sp.angle && (
                    <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate/40 dark:text-soft-white/30">
                      ({sp.angle})
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    ),
    copyText: content.seedPoints.map(s => `• ${s.seed}${s.angle ? ` (${s.angle})` : ''}`).join('\n'),
  });

  sections.push({
    id: 'titles',
    label: 'Titles',
    icon: Type,
    node:
      content.titles.length === 0 ? (
        <EmptySectionNote>No title suggestions were generated.</EmptySectionNote>
      ) : (
        <div className="space-y-2">
          {content.titles.map((t, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-sand/20 dark:border-slate/20"
            >
              <span className="text-sm text-midnight dark:text-soft-white">{t}</span>
              {onUseTitle && (
                <button
                  type="button"
                  onClick={() => onUseTitle(t)}
                  className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-electric hover:text-electric/80"
                >
                  <FilePlus2 className="h-3.5 w-3.5" /> Create sermon
                </button>
              )}
            </div>
          ))}
        </div>
      ),
    copyText: content.titles.join('\n'),
  });

  sections.push({
    id: 'crossReferences',
    label: 'Cross References',
    icon: GitBranch,
    node: (
      <div className="space-y-5">
        <div>
          {content.crossReferences.length === 0 ? (
            <EmptySectionNote>No cross references were suggested.</EmptySectionNote>
          ) : (
            <ul className="space-y-2">
              {content.crossReferences.map((c, i) => (
                <li key={i} className="text-sm">
                  <span className="font-semibold text-midnight dark:text-soft-white">{c.reference}</span>
                  {c.connection && <span className="text-slate dark:text-soft-white/60"> — {c.connection}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h4 className="text-xs font-semibold text-slate/50 dark:text-soft-white/40 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
            <Cross className="h-3.5 w-3.5 text-crimson" /> Christ-Centered
          </h4>
          <p className="text-sm text-midnight dark:text-soft-white/80 leading-relaxed">
            {content.christCentered || '—'}
          </p>
        </div>
        <div>
          <h4 className="text-xs font-semibold text-slate/50 dark:text-soft-white/40 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-gold" /> Common Misreadings
          </h4>
          {content.commonMisreadings.length === 0 ? (
            <EmptySectionNote>None flagged.</EmptySectionNote>
          ) : (
            <div className="space-y-2">
              {content.commonMisreadings.map((m, i) => (
                <div key={i} className="rounded-lg bg-gold/5 border border-gold/20 p-3">
                  <p className="text-sm text-midnight dark:text-soft-white">
                    <span className="font-semibold">Misreading:</span> {m.misreading}
                  </p>
                  <p className="text-sm text-slate dark:text-soft-white/60 mt-1">
                    <span className="font-semibold text-emerald">Correction:</span> {m.correction}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    ),
    copyText: [
      content.crossReferences.map(c => `${c.reference} — ${c.connection}`).join('\n'),
      `\nChrist-Centered:\n${content.christCentered}`,
      `\nCommon Misreadings:\n${content.commonMisreadings.map(m => `${m.misreading} → ${m.correction}`).join('\n')}`,
    ].join('\n'),
  });

  sections.push({
    id: 'serviceAssets',
    label: 'Service Assets',
    icon: Music,
    node: (
      <div className="space-y-4">
        <div>
          <h4 className="text-xs font-semibold text-slate/50 dark:text-soft-white/40 uppercase tracking-wider mb-1.5">
            Songs
          </h4>
          {content.serviceAssets.songs.length === 0 ? (
            <EmptySectionNote>No songs suggested.</EmptySectionNote>
          ) : (
            <div className="space-y-2">
              {content.serviceAssets.songs.map((s, i) => (
                <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg border border-sand/20 dark:border-slate/20">
                  <Music className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-midnight dark:text-soft-white">
                      {s.title}
                      {s.artist && (
                        <span className="font-normal text-slate dark:text-soft-white/50"> — {s.artist}</span>
                      )}
                    </p>
                    {s.reason && <p className="text-xs text-slate dark:text-soft-white/50 mt-0.5">{s.reason}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <h4 className="text-xs font-semibold text-slate/50 dark:text-soft-white/40 uppercase tracking-wider mb-1.5">
            Readings
          </h4>
          {content.serviceAssets.readings.length === 0 ? (
            <EmptySectionNote>No readings suggested.</EmptySectionNote>
          ) : (
            <ul className="list-disc list-inside text-sm text-midnight dark:text-soft-white/80 space-y-1">
              {content.serviceAssets.readings.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    ),
    copyText: [
      'Songs:',
      content.serviceAssets.songs.map(s => `${s.title}${s.artist ? ` — ${s.artist}` : ''}`).join('\n'),
      '\nReadings:',
      content.serviceAssets.readings.join('\n'),
    ].join('\n'),
  });

  return sections;
}

// ── Export: printable HTML page ─────────────────────────────────────────────
// The guide's text is model-generated — treat it as untrusted and escape
// everything that goes into the HTML string.

function escapeHtml(input: unknown): string {
  const s = typeof input === 'string' ? input : String(input ?? '');
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const PRINT_STYLE = `
  :root {
    --pg-midnight: #1C2B39;
    --pg-slate: #4F5D75;
    --pg-gold: #D9A441;
    --pg-electric: #2874A6;
    --pg-emerald: #2D7F5E;
    --pg-amber: #B45309;
  }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: Georgia, 'Times New Roman', serif;
    color: var(--pg-midnight);
    background: #ffffff;
    max-width: 760px;
    margin: 0 auto;
    padding: 56px 40px 96px;
    line-height: 1.65;
  }
  .eyebrow {
    font-family: -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--pg-slate);
    margin: 0 0 4px;
  }
  .doc-header { border-bottom: 2px solid var(--pg-gold); padding-bottom: 18px; margin-bottom: 36px; }
  .doc-header h1 { font-size: 30px; margin: 0 0 6px; }
  .doc-header .meta {
    font-family: -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: 12.5px;
    color: var(--pg-slate);
    margin: 0;
  }
  main section { margin-bottom: 32px; break-inside: avoid; page-break-inside: avoid; }
  main h2 {
    font-family: -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: 11.5px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--pg-slate);
    border-bottom: 1px solid #e6ddc8;
    padding-bottom: 8px;
    margin: 0 0 14px;
  }
  main h3 {
    font-family: -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--pg-slate);
    margin: 16px 0 6px;
  }
  .big-idea { font-size: 19px; line-height: 1.6; border-left: 3px solid var(--pg-gold); padding-left: 18px; margin: 0; }
  .kicker { font-family: -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif; font-size: 12px; font-style: italic; color: var(--pg-slate); margin: 0 0 12px; }
  .muted { color: var(--pg-slate); font-size: 13.5px; }
  p { margin: 0 0 10px; }
  ul { margin: 0 0 10px; padding-left: 22px; }
  li { margin-bottom: 6px; }
  .tag {
    display: inline-block;
    font-family: -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(40,116,166,0.1);
    color: var(--pg-electric);
    border: 1px solid rgba(40,116,166,0.25);
  }
  .tag-emerald { background: rgba(45,127,94,0.1); color: var(--pg-emerald); border-color: rgba(45,127,94,0.25); }
  .tag-amber { background: rgba(180,83,9,0.12); color: var(--pg-amber); border-color: rgba(180,83,9,0.3); }
  .source-item, .card, .misreading, .lexicon-entry {
    border: 1px solid #e6ddc8;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 10px;
  }
  .source-item-head, .card-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
  }
  .source-name, .word { font-weight: 600; }
  .word { font-size: 18px; font-family: Georgia, 'Times New Roman', serif; }
  .translit { font-style: italic; color: var(--pg-slate); margin-left: 6px; }
  .verse-ref { color: var(--pg-slate); font-size: 12px; margin-left: 8px; }
  .gloss { font-style: italic; margin: 6px 0 4px; }
  .lexicon-head { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px; }
  .app-card {
    border: 1px solid rgba(40,116,166,0.25);
    background: rgba(40,116,166,0.03);
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 14px;
    break-inside: avoid;
  }
  .app-head {
    font-family: -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-weight: 600;
    font-size: 13px;
    margin-bottom: 6px;
  }
  .app-body { font-size: 16px; margin: 6px 0; }
  .app-rooted { font-style: italic; color: var(--pg-gold); font-size: 13px; margin: 4px 0; }
  .app-tension { font-style: italic; color: var(--pg-slate); font-size: 13px; margin: 4px 0; }
  .app-seed {
    font-family: -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    background: rgba(217,164,65,0.1);
    border: 1px solid rgba(217,164,65,0.25);
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 12.5px;
    margin-top: 8px;
  }
  .doc-footer {
    margin-top: 48px;
    padding-top: 16px;
    border-top: 1px solid #e6ddc8;
    font-family: -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: 11px;
    color: var(--pg-slate);
  }
  .print-bar {
    position: sticky;
    bottom: 0;
    text-align: center;
    padding: 16px 0;
    background: #fff;
    border-top: 1px solid #e6ddc8;
    margin-top: 24px;
  }
  .print-bar button {
    font-family: -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    background: var(--pg-electric);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }
  @media print {
    body { padding: 24px 8px; max-width: none; }
    .print-bar { display: none !important; }
    main section, .app-card, .card, .source-item, .misreading, .lexicon-entry { break-inside: avoid; }
    a { color: inherit; text-decoration: none; }
  }
`;

function printableSectionsHtml(content: PassageGuideContent): string {
  const esc = escapeHtml;
  const parts: string[] = [];

  parts.push(`
    <section>
      <h2>Big Idea</h2>
      <p class="big-idea">${esc(content.bigIdea || 'No summary was generated for this passage.')}</p>
    </section>
  `);

  if (content.sourcesUsed.length > 0) {
    parts.push(`
      <section>
        <h2>Sources Used</h2>
        ${content.sourcesUsed
          .map(
            s => `
              <div class="source-item">
                <div class="source-item-head">
                  <span class="source-name">${esc(s.name || 'Unnamed source')}</span>
                  ${s.tradition ? `<span class="tag">${esc(s.tradition)}</span>` : ''}
                </div>
                ${s.whyRelevant ? `<p class="muted">${esc(s.whyRelevant)}</p>` : ''}
              </div>
            `
          )
          .join('')}
      </section>
    `);
  }

  if (content.wordStudy.entries.length > 0) {
    parts.push(`
      <section>
        <h2>Word Study${
          content.wordStudy.groundedInRobertson
            ? ' <span class="tag tag-emerald">Grounded in A.T. Robertson</span>'
            : ''
        }</h2>
        <p class="kicker">${esc(content.wordStudy.language)} word study</p>
        ${content.wordStudy.entries
          .map(
            e => `
              <div class="lexicon-entry">
                <div class="lexicon-head">
                  <span class="word">${esc(e.word || '—')}</span>
                  ${e.transliteration ? `<span class="translit">${esc(e.transliteration)}</span>` : ''}
                  ${e.verse ? `<span class="verse-ref">${esc(e.verse)}</span>` : ''}
                  ${e.confidence === 'verify' ? '<span class="tag tag-amber">Verify in a lexicon</span>' : ''}
                </div>
                ${e.gloss ? `<p class="gloss">${esc(e.gloss)}</p>` : ''}
                ${e.significance ? `<p class="muted">${esc(e.significance)}</p>` : ''}
              </div>
            `
          )
          .join('')}
      </section>
    `);
  }

  if (content.context.historical || content.context.literary || content.context.canonical) {
    parts.push(`
      <section>
        <h2>Context</h2>
        ${content.context.historical ? `<h3>Historical</h3><p>${esc(content.context.historical)}</p>` : ''}
        ${content.context.literary ? `<h3>Literary</h3><p>${esc(content.context.literary)}</p>` : ''}
        ${content.context.canonical ? `<h3>Canonical</h3><p>${esc(content.context.canonical)}</p>` : ''}
      </section>
    `);
  }

  if (content.insights.length > 0) {
    parts.push(`
      <section>
        <h2>Insights</h2>
        ${content.insights
          .map(
            i => `
              <div class="card">
                <div class="card-head">
                  <p>${esc(i.insight)}</p>
                  ${i.attributedTo ? `<span class="tag">${esc(i.attributedTo)}</span>` : ''}
                </div>
                ${i.application ? `<p class="muted"><strong>Application:</strong> ${esc(i.application)}</p>` : ''}
              </div>
            `
          )
          .join('')}
      </section>
    `);
  }

  if (content.applications.length > 0) {
    parts.push(`
      <section>
        <h2>Application</h2>
        ${content.applications
          .map(
            a => `
              <div class="app-card">
                ${
                  a.audience || a.situation
                    ? `<div class="app-head">${esc(a.audience)}${a.situation ? ` — ${esc(a.situation)}` : ''}</div>`
                    : ''
                }
                ${a.application ? `<p class="app-body">${esc(a.application)}</p>` : ''}
                ${a.rootedIn ? `<p class="app-rooted">${esc(a.rootedIn)}</p>` : ''}
                ${a.tension ? `<p class="app-tension">${esc(a.tension)}</p>` : ''}
                ${
                  a.illustrationSeed
                    ? `<p class="app-seed"><strong>Illustration seed:</strong> ${esc(a.illustrationSeed)}</p>`
                    : ''
                }
              </div>
            `
          )
          .join('')}
      </section>
    `);
  }

  if (content.seedPoints.length > 0) {
    parts.push(`
      <section>
        <h2>Seed Points</h2>
        <p class="kicker">Seed ideas to spark your own study — not an outline</p>
        <ul>
          ${content.seedPoints
            .map(
              sp =>
                `<li>${esc(sp.seed)}${sp.angle ? ` <span class="muted">(${esc(sp.angle)})</span>` : ''}</li>`
            )
            .join('')}
        </ul>
      </section>
    `);
  }

  if (content.titles.length > 0) {
    parts.push(`
      <section>
        <h2>Titles</h2>
        <ul>${content.titles.map(t => `<li>${esc(t)}</li>`).join('')}</ul>
      </section>
    `);
  }

  if (content.crossReferences.length > 0 || content.christCentered || content.commonMisreadings.length > 0) {
    parts.push(`
      <section>
        <h2>Cross References</h2>
        ${
          content.crossReferences.length > 0
            ? `<ul>${content.crossReferences
                .map(c => `<li><strong>${esc(c.reference)}</strong>${c.connection ? ` — ${esc(c.connection)}` : ''}</li>`)
                .join('')}</ul>`
            : ''
        }
        ${content.christCentered ? `<h3>Christ-Centered</h3><p>${esc(content.christCentered)}</p>` : ''}
        ${
          content.commonMisreadings.length > 0
            ? `<h3>Common Misreadings</h3>${content.commonMisreadings
                .map(
                  m => `
                    <div class="misreading">
                      <p><strong>Misreading:</strong> ${esc(m.misreading)}</p>
                      <p><strong>Correction:</strong> ${esc(m.correction)}</p>
                    </div>
                  `
                )
                .join('')}`
            : ''
        }
      </section>
    `);
  }

  if (content.serviceAssets.songs.length > 0 || content.serviceAssets.readings.length > 0) {
    parts.push(`
      <section>
        <h2>Service Assets</h2>
        ${
          content.serviceAssets.songs.length > 0
            ? `<h3>Songs</h3><ul>${content.serviceAssets.songs
                .map(
                  s =>
                    `<li>${esc(s.title)}${s.artist ? ` — ${esc(s.artist)}` : ''}${
                      s.reason ? `<br><span class="muted">${esc(s.reason)}</span>` : ''
                    }</li>`
                )
                .join('')}</ul>`
            : ''
        }
        ${
          content.serviceAssets.readings.length > 0
            ? `<h3>Readings</h3><ul>${content.serviceAssets.readings.map(r => `<li>${esc(r)}</li>`).join('')}</ul>`
            : ''
        }
      </section>
    `);
  }

  return parts.join('');
}

function buildPrintableGuideHtml(guide: ActiveGuide): string {
  const esc = escapeHtml;
  const generatedDate = new Date(guide.createdAt).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const metaBits = [guide.translation, guide.denominationUsed, `Generated ${generatedDate}`].filter(
    (b): b is string => !!b
  );

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${esc(`Passage Guide — ${guide.scriptureReference}`)}</title>
<style>${PRINT_STYLE}</style>
</head>
<body>
  <header class="doc-header">
    <p class="eyebrow">Passage Guide</p>
    <h1>${esc(guide.scriptureReference)}</h1>
    <p class="meta">${metaBits.map(esc).join(' &middot; ')}</p>
  </header>
  <main>
    ${printableSectionsHtml(guide.content)}
  </main>
  <footer class="doc-footer">
    <p>Created with SermonBuild</p>
  </footer>
  <div class="print-bar">
    <button type="button" onclick="window.print()">Print / Save as PDF</button>
  </div>
</body>
</html>`;
}

// ── History list ─────────────────────────────────────────────────────────────

function HistoryList({
  items,
  loading,
  onSelect,
  onArchive,
}: {
  items: PassageGuideRow[];
  loading: boolean;
  onSelect: (row: PassageGuideRow) => void;
  onArchive: (id: string) => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-electric" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-slate/50 dark:text-soft-white/40">
        No saved passage guides yet.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map(row => (
        <div
          key={row.id}
          className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg border border-sand/20 dark:border-slate/20 hover:border-electric/40 transition-colors"
        >
          <button type="button" onClick={() => onSelect(row)} className="flex-1 min-w-0 text-left">
            <p className="text-sm font-semibold text-midnight dark:text-soft-white truncate">
              {row.scripture_reference}
            </p>
            <p className="text-xs text-slate/50 dark:text-soft-white/40">
              {new Date(row.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
              {row.translation ? ` · ${row.translation}` : ''}
            </p>
          </button>
          <button
            type="button"
            onClick={() => onArchive(row.id)}
            title="Archive"
            aria-label={`Archive guide for ${row.scripture_reference}`}
            className="flex-shrink-0 p-1.5 text-slate/50 dark:text-soft-white/40 hover:text-crimson rounded-lg hover:bg-crimson/5 transition-colors"
          >
            <Archive className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Accordion (panel mode) ──────────────────────────────────────────────────

function Accordion({
  sections,
  openId,
  onToggle,
}: {
  sections: Section[];
  openId: string | null;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="space-y-2">
      {sections.map(section => {
        const isOpen = openId === section.id;
        const panelId = `pg-panel-${section.id}`;
        return (
          <div key={section.id} className="rounded-xl border border-sand/20 dark:border-slate/20 overflow-hidden">
            <button
              type="button"
              onClick={() => onToggle(section.id)}
              aria-expanded={isOpen}
              aria-controls={panelId}
              className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-sand/5 dark:hover:bg-slate/10 transition-colors"
            >
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate/70 dark:text-soft-white/60">
                <section.icon className="h-3.5 w-3.5 text-slate/40 dark:text-soft-white/30 flex-shrink-0" />
                {section.label}
                {section.badge}
              </span>
              {isOpen ? (
                <ChevronUp className="h-4 w-4 text-slate/50 flex-shrink-0" />
              ) : (
                <ChevronDown className="h-4 w-4 text-slate/50 flex-shrink-0" />
              )}
            </button>
            {isOpen && (
              <div id={panelId} className="px-4 py-4 border-t border-sand/20 dark:border-slate/20">
                {section.node}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export function PassageGuide({ mode = 'page', defaultSermonId, sermonScripture }: PassageGuideProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Input state
  const [reference, setReference] = useState(sermonScripture ?? '');
  const [passageText, setPassageText] = useState<string | null>(null);
  const [versionId, setVersionId] = useState<string>(bibleVersions[0].id);
  const [translationOverride, setTranslationOverride] = useState<string | null>(null);
  const [showBibleSelector, setShowBibleSelector] = useState(false);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [rateLimitInfo, setRateLimitInfo] = useState<{ used: number; limit: number } | null>(null);
  const [stageIndex, setStageIndex] = useState(0);
  const [stoppedNotice, setStoppedNotice] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  // Result state
  const [guide, setGuide] = useState<ActiveGuide | null>(null);
  const [persistError, setPersistError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState(false);

  // History state
  const [history, setHistory] = useState<PassageGuideRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Accordion (panel mode)
  const [openSectionId, setOpenSectionId] = useState<string | null>('bigIdea');

  // Create sermon
  const [creatingSermon, setCreatingSermon] = useState(false);
  const [createSermonError, setCreateSermonError] = useState<string | null>(null);

  // Link to sermon
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const translation = translationOverride ?? (bibleVersions.find(v => v.id === versionId)?.short ?? 'KJV');

  const loadHistory = useCallback(async () => {
    if (!user) {
      setHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const { data, error } = await supabase
        .from('passage_guides')
        .select('*')
        .eq('user_id', user.id)
        .eq('archived', false)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setHistory((data ?? []) as PassageGuideRow[]);
    } catch (err) {
      logger.error('Failed to load passage guide history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Staged loading label — advances on a timer so a 60-90s call never looks frozen.
  useEffect(() => {
    if (!generating) {
      setStageIndex(0);
      return;
    }
    const id = window.setInterval(() => {
      setStageIndex(i => Math.min(i + 1, LOADING_STAGES.length - 1));
    }, STAGE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [generating]);

  // Abort any in-flight generation on unmount so navigating away never leaves
  // a dangling request or triggers a state update after unmount.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  const handleReferenceChange = (value: string) => {
    setReference(value);
    // The cached passage text no longer matches an edited reference.
    setPassageText(null);
  };

  const handleBibleSelect = (passage: { reference: string; text: string; version: string }) => {
    setReference(passage.reference);
    setPassageText(passage.text || null);
    const matched = bibleVersions.find(v => v.short === passage.version);
    if (matched) {
      setVersionId(matched.id);
      setTranslationOverride(null);
    } else {
      setTranslationOverride(passage.version || null);
    }
    setShowBibleSelector(false);
  };

  const generate = useCallback(async () => {
    const trimmedRef = reference.trim();
    if (!trimmedRef || !user) return;

    // Abort any previous in-flight generation before starting a new one.
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setGenerating(true);
    setGenError(null);
    setRateLimitInfo(null);
    setPersistError(null);
    setStoppedNotice(false);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!isMountedRef.current) return;
      if (!session) throw new Error('Please sign in to generate a passage guide.');

      let textForRequest = passageText;
      if (!textForRequest) {
        try {
          const fetchVersionId = bibleVersions.find(v => v.short === translation)?.id ?? versionId;
          const fetched = await fetchBiblePassage(trimmedRef, fetchVersionId);
          textForRequest = fetched.text || null;
          if (textForRequest && isMountedRef.current) setPassageText(textForRequest);
        } catch (fetchErr) {
          logger.warn('Could not fetch passage text before generating; continuing without it:', fetchErr);
          textForRequest = null;
        }
      }

      if (!isMountedRef.current) return;

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-passage-guide`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          reference: trimmedRef,
          translation,
          passageText: textForRequest,
          sermonId: defaultSermonId ?? null,
        }),
        signal: controller.signal,
      });

      if (res.status === 429) {
        const rateData = await res.json().catch(() => null);
        if (!isMountedRef.current) return;
        const used = typeof rateData?.used === 'number' ? rateData.used : 0;
        const limit = typeof rateData?.limit === 'number' ? rateData.limit : 0;
        setRateLimitInfo({ used, limit });
        return;
      }

      // Every other early-exit (auth, subscription, bad reference, missing config)
      // returns a plain small JSON/text body straight away — only the actual
      // generation call below is streamed, so it's the only response we need to
      // read as a stream rather than parse directly.
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        if (!isMountedRef.current) return;
        throw new Error(errBody?.error || 'Failed to generate the passage guide. Please try again.');
      }

      // A long passage can take well past a minute to generate, so the function
      // streams its response to avoid the edge platform's idle-response timeout —
      // everything before RESULT_MARKER is the model's own raw (partial) text and
      // is discarded; the JSON envelope after it is the real payload.
      const RESULT_MARKER = "\u0000\u0000PASSAGE_GUIDE_RESULT\u0000\u0000";
      const rawBody = await res.text();
      if (!isMountedRef.current) return;
      const markerIndex = rawBody.indexOf(RESULT_MARKER);
      const data = markerIndex !== -1
        ? JSON.parse(rawBody.slice(markerIndex + RESULT_MARKER.length))
        : null;

      if (!data || data.success === false || !data.guide) {
        throw new Error(data?.error || 'Failed to generate the passage guide. Please try again.');
      }

      const content = normalizeContent(data.guide);
      const denominationUsed: string | null = typeof data.denominationUsed === 'string' ? data.denominationUsed : null;

      let newGuide: ActiveGuide = {
        id: null,
        sermonId: defaultSermonId ?? null,
        scriptureReference: content.reference || trimmedRef,
        translation,
        denominationUsed,
        content,
        createdAt: new Date().toISOString(),
      };

      // Persist client-side — fail soft. The generated guide is never lost
      // just because the save failed.
      try {
        const { data: inserted, error: insertErr } = await supabase
          .from('passage_guides')
          .insert([
            {
              user_id: user.id,
              sermon_id: defaultSermonId ?? null,
              title: `Passage Guide — ${newGuide.scriptureReference}`,
              scripture_reference: newGuide.scriptureReference,
              translation,
              denomination_used: denominationUsed,
              content,
            },
          ])
          .select()
          .single();

        if (insertErr) throw insertErr;
        if (inserted) {
          newGuide = rowToActiveGuide(inserted as PassageGuideRow);
          if (isMountedRef.current) setSaveNotice(true);
        }
      } catch (persistErr) {
        logger.error('Failed to save passage guide:', persistErr);
        if (isMountedRef.current) {
          setPersistError("This guide couldn't be saved to your history, but it's shown below.");
        }
      }

      if (!isMountedRef.current) return;
      setGuide(newGuide);
      setOpenSectionId('bigIdea');
      loadHistory();
    } catch (err) {
      if (!isMountedRef.current) return;
      if (isAbortError(err)) {
        // The daily quota is consumed server-side as soon as the request
        // starts, so stopping never refunds it — the UI copy says so.
        setStoppedNotice(true);
        return;
      }
      logger.error('Passage guide generation failed:', err);
      setGenError(err instanceof Error ? err.message : 'Failed to generate the passage guide. Please try again.');
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
      if (isMountedRef.current) {
        setGenerating(false);
      }
    }
  }, [reference, translation, passageText, versionId, defaultSermonId, user, loadHistory]);

  const regenerate = () => {
    generate();
  };

  const stopGeneration = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const handleSelectHistory = (row: PassageGuideRow) => {
    // Don't let a generation that's still in flight overwrite the history
    // item the user just picked.
    abortControllerRef.current?.abort();
    setGuide(rowToActiveGuide(row));
    setReference(row.scripture_reference);
    const matched = bibleVersions.find(v => v.short === row.translation);
    if (matched) {
      setVersionId(matched.id);
      setTranslationOverride(null);
    } else {
      setTranslationOverride(row.translation ?? null);
    }
    setPassageText(null);
    setOpenSectionId('bigIdea');
    setHistoryOpen(false);
    setGenError(null);
    setRateLimitInfo(null);
    setPersistError(null);
    setCreateSermonError(null);
    setLinkError(null);
    setStoppedNotice(false);
  };

  const handleArchive = async (id: string) => {
    try {
      const { error } = await supabase.from('passage_guides').update({ archived: true }).eq('id', id);
      if (error) throw error;
      setHistory(prev => prev.filter(r => r.id !== id));
      setGuide(g => (g && g.id === id ? null : g));
    } catch (err) {
      logger.error('Failed to archive passage guide:', err);
    }
  };

  const linkToSermon = async () => {
    if (!guide?.id || !defaultSermonId) return;
    setLinking(true);
    setLinkError(null);
    try {
      const { error } = await supabase
        .from('passage_guides')
        .update({ sermon_id: defaultSermonId })
        .eq('id', guide.id);
      if (error) throw error;
      setGuide(g => (g ? { ...g, sermonId: defaultSermonId } : g));
      loadHistory();
    } catch (err) {
      logger.error('Failed to link passage guide to sermon:', err);
      setLinkError('Could not link this guide to the sermon. Please try again.');
    } finally {
      setLinking(false);
    }
  };

  const createSermonFromGuide = useCallback(
    async (title: string) => {
      if (!guide || !user) return;
      setCreatingSermon(true);
      setCreateSermonError(null);
      try {
        const { data: newSermon, error } = await supabase
          .from('sermons')
          .insert([
            {
              user_id: user.id,
              title: title.trim() || `Sermon on ${guide.scriptureReference}`,
              scripture: guide.scriptureReference,
              content: '',
              status: 'draft',
              series_id: null,
              date: null,
              archived: false,
              version: 1,
            },
          ])
          .select()
          .single();

        if (error) throw error;
        navigate(`/dashboard/editor/${newSermon.id}`);
      } catch (err) {
        logger.error('Failed to create sermon from passage guide:', err);
        setCreateSermonError(err instanceof Error ? err.message : 'Failed to create sermon. Please try again.');
      } finally {
        setCreatingSermon(false);
      }
    },
    [guide, user, navigate]
  );

  const sections = useMemo(() => {
    if (!guide) return [];
    const onUseTitle = mode === 'page' ? (title: string) => { void createSermonFromGuide(title); } : undefined;
    return buildSections(guide.content, onUseTitle);
  }, [guide, mode, createSermonFromGuide]);

  const canLink = !!(defaultSermonId && guide?.id && !guide.sermonId);

  // Opens a self-contained, print-friendly HTML page of the full brief in a
  // new tab. This is the primary export path — the browser's own "Print" /
  // "Save as PDF" (Cmd/Ctrl+P) turns it into a PDF, so no extra file format
  // needs to be generated here.
  const openPrintableGuide = useCallback(() => {
    if (!guide) return;
    const html = buildPrintableGuideHtml(guide);
    const win = window.open('', '_blank');
    if (!win) {
      logger.warn('Could not open the printable guide — the popup may have been blocked.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  }, [guide]);

  // Secondary export path: a direct PDF download built from the same
  // section text used for on-screen copy buttons, via the shared PDF
  // helpers already used elsewhere in the app (see src/lib/pdf.ts).
  const downloadGuidePDF = useCallback(() => {
    if (!guide) return;
    const doc = new jsPDF();
    const generatedDate = new Date(guide.createdAt).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const metaBits = [guide.translation, guide.denominationUsed].filter((b): b is string => !!b).join(' · ');

    let y = createStyledPDF(
      {
        title: 'Passage Guide',
        subtitle: guide.scriptureReference,
        date: metaBits ? `${metaBits} · ${generatedDate}` : generatedDate,
      },
      doc
    );

    sections.forEach(section => {
      const lines = cleanAIArray(section.copyText.split('\n'));
      if (lines.length === 0) return;
      y = addSection(doc, section.label, lines, y);
    });

    const slug = (guide.scriptureReference || 'passage-guide')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    doc.save(`passage-guide-${slug || 'export'}.pdf`);
  }, [guide, sections]);

  // ── Input form (shared shell for both modes) ──────────────────────────────
  const inputForm = (
    <div className="bg-white dark:bg-charcoal rounded-xl border border-sand/20 dark:border-slate/20 p-5 space-y-4">
      <div>
        <label
          htmlFor="pg-reference"
          className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-1.5"
        >
          Scripture Reference
        </label>
        <div className="flex gap-2">
          <input
            id="pg-reference"
            type="text"
            value={reference}
            onChange={e => handleReferenceChange(e.target.value)}
            placeholder="e.g., Romans 8:1-11"
            className="flex-1 min-w-0 rounded-lg border border-sand/30 dark:border-slate/30 bg-white dark:bg-charcoal text-midnight dark:text-soft-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-electric/30"
          />
          <button
            type="button"
            onClick={() => setShowBibleSelector(true)}
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-sand/30 dark:border-slate/30 text-slate dark:text-soft-white/60 hover:border-electric/50 hover:text-electric text-sm font-medium whitespace-nowrap"
          >
            <BookOpen className="h-3.5 w-3.5" /> Pick passage
          </button>
        </div>
        {passageText && (
          <p className="text-[11px] text-emerald mt-1.5 flex items-center gap-1">
            <Check className="h-3 w-3" /> Passage text loaded
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="pg-translation"
          className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-1.5"
        >
          Translation
        </label>
        <select
          id="pg-translation"
          value={versionId}
          onChange={e => {
            setVersionId(e.target.value);
            setTranslationOverride(null);
          }}
          className="w-full rounded-lg border border-sand/30 dark:border-slate/30 bg-white dark:bg-charcoal text-midnight dark:text-soft-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-electric/30"
        >
          {bibleVersions.map(v => (
            <option key={v.id} value={v.id}>
              {v.name} ({v.short})
            </option>
          ))}
        </select>
        {translationOverride && (
          <p className="text-[11px] text-slate/50 dark:text-soft-white/40 mt-1.5">
            Using {translationOverride} from your picked passage.
          </p>
        )}
      </div>

      {genError && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-crimson/10 text-crimson text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {genError}
        </div>
      )}
      {rateLimitInfo && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-gold/10 text-gold text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          You've used {rateLimitInfo.used} of {rateLimitInfo.limit} passage guides today. Your limit resets every 24
          hours.
        </div>
      )}
      {stoppedNotice && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-sand/40 dark:bg-slate/15 text-slate dark:text-soft-white/60 text-sm">
          <Info className="h-4 w-4 flex-shrink-0" />
          Generation stopped. This still counted toward today's limit.
        </div>
      )}

      <button
        type="button"
        onClick={generating ? stopGeneration : generate}
        disabled={!generating && !reference.trim()}
        className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
          generating
            ? 'bg-crimson/90 text-white hover:bg-crimson'
            : 'bg-electric text-white hover:bg-electric/90 shadow-sm shadow-electric/20'
        }`}
      >
        {generating ? (
          <>
            <Square className="h-4 w-4 fill-current" /> Stop
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" /> Generate Guide
          </>
        )}
      </button>

      {generating && <StagedLoadingBlock stageIndex={stageIndex} />}
    </div>
  );

  const bibleSelectorModal = showBibleSelector && (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Select Bible passage"
      onClick={() => setShowBibleSelector(false)}
    >
      <div onClick={e => e.stopPropagation()}>
        <BibleSelector onSelect={handleBibleSelect} onClose={() => setShowBibleSelector(false)} />
      </div>
    </div>
  );

  const saveNotification = (
    <SaveNotification
      show={saveNotice}
      onHide={() => setSaveNotice(false)}
      message="Passage guide saved"
      type="success"
    />
  );

  // ── Panel mode ──────────────────────────────────────────────────────────
  if (mode === 'panel') {
    return (
      <div className="flex flex-col h-full min-h-0">
        <div className="sticky top-0 z-10 bg-white dark:bg-charcoal border-b border-sand/20 dark:border-slate/20 px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Compass className="h-4 w-4 text-electric flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-midnight dark:text-soft-white truncate">
                  {guide ? guide.scriptureReference : 'Passage Guide'}
                </p>
                {guide?.translation && (
                  <p className="text-[11px] text-slate/50 dark:text-soft-white/40">{guide.translation}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => setHistoryOpen(o => !o)}
                title="Previous guides"
                aria-label="Previous guides"
                aria-expanded={historyOpen}
                className="p-1.5 rounded-lg text-slate dark:text-soft-white/50 hover:text-electric hover:bg-sand/20 dark:hover:bg-slate/20 transition-colors"
              >
                <History className="h-4 w-4" />
              </button>
              {guide && (
                <>
                  <button
                    type="button"
                    onClick={openPrintableGuide}
                    title="Print / export guide"
                    aria-label="Open a printable version of this guide"
                    className="p-1.5 rounded-lg text-slate dark:text-soft-white/50 hover:text-electric hover:bg-sand/20 dark:hover:bg-slate/20 transition-colors"
                  >
                    <Printer className="h-4 w-4" />
                  </button>
                  {canLink && (
                    <button
                      type="button"
                      onClick={linkToSermon}
                      disabled={linking}
                      title="Link to this sermon"
                      aria-label="Link to this sermon"
                      className="p-1.5 rounded-lg text-slate dark:text-soft-white/50 hover:text-electric hover:bg-sand/20 dark:hover:bg-slate/20 disabled:opacity-50 transition-colors"
                    >
                      <Link2 className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={generating ? stopGeneration : regenerate}
                    title={generating ? 'Stop generation' : 'Regenerate'}
                    aria-label={generating ? 'Stop generation' : 'Regenerate this guide'}
                    className="p-1.5 rounded-lg text-slate dark:text-soft-white/50 hover:text-electric hover:bg-sand/20 dark:hover:bg-slate/20 transition-colors"
                  >
                    {generating ? (
                      <Square className="h-4 w-4 fill-current text-crimson" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => { stopGeneration(); setGuide(null); }}
                    title="New guide"
                    aria-label="Start a new guide"
                    className="p-1.5 rounded-lg text-slate dark:text-soft-white/50 hover:text-electric hover:bg-sand/20 dark:hover:bg-slate/20 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {historyOpen ? (
            <HistoryList items={history} loading={historyLoading} onSelect={handleSelectHistory} onArchive={handleArchive} />
          ) : !guide ? (
            inputForm
          ) : generating ? (
            <StagedLoadingBlock stageIndex={stageIndex} />
          ) : (
            <>
              {guide.denominationUsed === null && <DenominationNudge />}
              {persistError && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gold/10 text-gold text-xs">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                  {persistError}
                </div>
              )}
              {linkError && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-crimson/10 text-crimson text-xs">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                  {linkError}
                </div>
              )}
              {stoppedNotice && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-sand/40 dark:bg-slate/15 text-slate dark:text-soft-white/60 text-xs">
                  <Info className="h-3.5 w-3.5 flex-shrink-0" />
                  Generation stopped. This still counted toward today's limit.
                </div>
              )}
              <Accordion sections={sections} openId={openSectionId} onToggle={id => setOpenSectionId(prev => (prev === id ? null : id))} />
            </>
          )}
        </div>

        {bibleSelectorModal}
        {saveNotification}
      </div>
    );
  }

  // ── Page mode ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h2 className="text-xl font-semibold text-midnight dark:text-soft-white flex items-center gap-2">
            <Compass className="h-5 w-5 text-electric" /> Passage Guide
          </h2>
          <p className="text-sm text-slate dark:text-soft-white/50 mt-0.5">
            A research brief for the passage you're about to preach — word study, tradition-rooted insights, and
            seed ideas.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setHistoryOpen(o => !o)}
          aria-expanded={historyOpen}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-sand/40 dark:bg-slate/20 text-slate dark:text-soft-white/60 hover:bg-sand dark:hover:bg-slate/30 transition-colors"
        >
          <History className="h-4 w-4" />
          Previous guides{history.length > 0 ? ` (${history.length})` : ''}
          {historyOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>

      {historyOpen ? (
        <HistoryList items={history} loading={historyLoading} onSelect={handleSelectHistory} onArchive={handleArchive} />
      ) : !guide ? (
        inputForm
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-3 bg-white dark:bg-charcoal rounded-xl border border-sand/20 dark:border-slate/20 p-5">
            <div>
              <p className="text-xs font-semibold text-slate/50 dark:text-soft-white/40 uppercase tracking-wider mb-1">
                Passage Guide
              </p>
              <h3 className="text-xl font-bold text-midnight dark:text-soft-white">{guide.scriptureReference}</h3>
              {guide.translation && (
                <p className="text-xs text-slate/50 dark:text-soft-white/40 mt-0.5">{guide.translation}</p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => { stopGeneration(); setGuide(null); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-sand/40 dark:bg-slate/20 text-slate dark:text-soft-white/60 hover:bg-sand dark:hover:bg-slate/30 transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" /> New guide
              </button>
              <button
                type="button"
                onClick={generating ? stopGeneration : regenerate}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  generating
                    ? 'bg-crimson/10 text-crimson hover:bg-crimson/15'
                    : 'bg-sand/40 dark:bg-slate/20 text-slate dark:text-soft-white/60 hover:bg-sand dark:hover:bg-slate/30'
                }`}
              >
                {generating ? <Square className="h-3.5 w-3.5 fill-current" /> : <Sparkles className="h-3.5 w-3.5" />}
                {generating ? 'Stop' : 'Regenerate'}
              </button>
              <button
                type="button"
                onClick={openPrintableGuide}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-sand/40 dark:bg-slate/20 text-slate dark:text-soft-white/60 hover:bg-sand dark:hover:bg-slate/30 transition-colors"
              >
                <Printer className="h-3.5 w-3.5" /> Print / Export
              </button>
              <button
                type="button"
                onClick={downloadGuidePDF}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-sand/40 dark:bg-slate/20 text-slate dark:text-soft-white/60 hover:bg-sand dark:hover:bg-slate/30 transition-colors"
              >
                <Download className="h-3.5 w-3.5" /> Download PDF
              </button>
              {canLink && (
                <button
                  type="button"
                  onClick={linkToSermon}
                  disabled={linking}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-sand/40 dark:bg-slate/20 text-slate dark:text-soft-white/60 hover:bg-sand dark:hover:bg-slate/30 disabled:opacity-50 transition-colors"
                >
                  {linking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
                  Link to this sermon
                </button>
              )}
              <button
                type="button"
                onClick={() => createSermonFromGuide(guide.content.titles[0] ?? `Sermon on ${guide.scriptureReference}`)}
                disabled={creatingSermon}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-electric text-white hover:bg-electric/90 disabled:opacity-50 transition-colors"
              >
                {creatingSermon ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FilePlus2 className="h-3.5 w-3.5" />}
                Create sermon from this guide
              </button>
            </div>
          </div>

          {guide.denominationUsed === null && <DenominationNudge />}
          {persistError && (
            <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg bg-gold/10 text-gold text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {persistError}
            </div>
          )}
          {linkError && (
            <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg bg-crimson/10 text-crimson text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {linkError}
            </div>
          )}
          {stoppedNotice && (
            <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg bg-sand/40 dark:bg-slate/15 text-slate dark:text-soft-white/60 text-sm">
              <Info className="h-4 w-4 flex-shrink-0" />
              Generation stopped. This still counted toward today's limit.
            </div>
          )}
          {createSermonError && (
            <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg bg-crimson/10 text-crimson text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {createSermonError}
            </div>
          )}

          {generating ? (
            <div className="bg-white dark:bg-charcoal rounded-xl border border-sand/20 dark:border-slate/20">
              <StagedLoadingBlock stageIndex={stageIndex} />
            </div>
          ) : (
            sections.map(section => (
              <div
                key={section.id}
                className="bg-white dark:bg-charcoal rounded-xl border border-sand/20 dark:border-slate/20 overflow-hidden"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3.5 border-b border-sand/15 dark:border-slate/15">
                  <div className="flex items-center gap-2.5">
                    <section.icon className="h-4 w-4 text-slate/40 dark:text-soft-white/30" />
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate/70 dark:text-soft-white/60">
                      {section.label}
                    </h3>
                    {section.badge}
                  </div>
                  <CopyButton text={section.copyText} label={section.label} />
                </div>
                <div className="p-5">{section.node}</div>
              </div>
            ))
          )}
        </div>
      )}

      {bibleSelectorModal}
      {saveNotification}
    </div>
  );
}
